import {
  addDays,
  MAX_EFFECTIVE_DAYS_AHEAD,
  OPEN_REVISION_STATES,
  type RevisionState,
} from '@felix/domain';
import type { DocumentPermissions, RevisionAction } from './source.js';
import type {
  DistributionCell,
  DistributionRow,
  LibraryDetail,
  LibraryDocument,
  LibraryFolder,
  LibraryRevision,
  LibraryTree,
} from './types.js';

// ---------------------------------------------------------------------------------------------
// Onay akışı (ADR 0027 §4, §10; gereksinim §3.2): taslak → onaya gönder → onayla → yayınla
// ---------------------------------------------------------------------------------------------

/** İşlemin istediği girdiler: gerekçe, parola (§7.1, kapatılamaz) ve yürürlük günü (#27). */
export const ACTION_INPUTS: Readonly<
  Record<RevisionAction, { reason: boolean; password: boolean; effectiveOn: boolean }>
> = {
  submit: { reason: false, password: false, effectiveOn: false },
  return: { reason: true, password: false, effectiveOn: false },
  approve: { reason: false, password: true, effectiveOn: false },
  publish: { reason: false, password: true, effectiveOn: true },
  withdraw: { reason: true, password: true, effectiveOn: false },
  discard: { reason: true, password: false, effectiveOn: false },
  // Gemi kütüphanesi (ADR 0038 §10): yeni yürürlük günü zorunlu
  reschedule: { reason: true, password: true, effectiveOn: true },
  cancel: { reason: true, password: true, effectiveOn: false },
};

/** Geri alınamaz ya da kaydı kapatan işlemler kırmızı düğmeyle sorulur. */
export const DANGER_ACTIONS: readonly RevisionAction[] = [
  'withdraw',
  'discard',
  'return',
  'cancel',
];

/** Dalın açık revizyonu (taslak, onay bekleyen, onaylanmış, zamanlanmış; dalda en çok bir). */
export function openRevision(detail: LibraryDetail): LibraryRevision | null {
  return (
    [...detail.revisions]
      .filter((r) => OPEN_REVISION_STATES.includes(r.state))
      .sort((a, b) => b.seqNo - a.seqNo)[0] ?? null
  );
}

/**
 * Her işlemin istediği yetkiler: sunucunun eşlemesiyle aynı (`authorizer.ts`
 * DOCUMENT_EVENT_ACTIONS; inceleme M8a3-R18). Hepsi gerekir.
 */
const ACTION_PERMISSIONS: Readonly<
  Record<RevisionAction, readonly ('create' | 'approve' | 'submit' | 'return' | 'void')[]>
> = {
  submit: ['submit'],
  discard: ['void', 'create'],
  approve: ['approve'],
  return: ['return'],
  publish: ['approve'],
  withdraw: ['approve', 'void'],
  reschedule: ['approve'],
  cancel: ['approve', 'void'],
};

/**
 * Revizyonun durumunda kişinin yapabileceği işlemler (düğme sırası). Sunucu yetkiyi, dört göz
 * kuralını (yükleyen ve gönderen onaylayamaz [sorular #22]) ve içerik hash'ini yeniden denetler.
 * - Taslak: onaya gönder (`submit`), vazgeç (`void` + `create`).
 * - Onay bekleyen: onayla (`approve`), iade et (`return`).
 * - Onaylanmış: yayınla (`approve`), geri çek (`approve` + `void`); zamanlanmış ve yürürlükte:
 *   geri çek.
 * - Geminin eski belgesinde (gemide; ADR 0081) yalnız açık kalanı kapatma: taslaktan vazgeç,
 *   onay bekleyeni iade et, zamanlanmışı iptal et, onaylı ya da yürürlüktekini geri çek. Gönderme,
 *   onay, yayın ve erteleme yoktur: dokümanı yalnız şirket yayınlar [ISM 11.1, 11.2.2]. Ofisin
 *   şirket ekranı değişmez.
 */
export function workflowActions(
  revision: LibraryRevision,
  permissions: DocumentPermissions,
  library: 'company' | 'vessel' = 'company',
): RevisionAction[] {
  const byState: Partial<Record<RevisionState, RevisionAction[]>> =
    library === 'vessel'
      ? {
          draft: ['discard'],
          pending_approval: ['return'],
          approved: ['withdraw'],
          scheduled: ['cancel'],
          published: ['withdraw'],
        }
      : {
          draft: ['submit', 'discard'],
          pending_approval: ['approve', 'return'],
          approved: ['publish', 'withdraw'],
          scheduled: ['withdraw'],
          published: ['withdraw'],
        };
  return (byState[revision.state] ?? []).filter((action) =>
    ACTION_PERMISSIONS[action].every((name) => permissions[name] === true),
  );
}

/** Onay ya da yayın bekleyen doküman ("Pending approval" süzgeci; panodaki uyarı, §7.3). */
export function awaitingApproval(document: LibraryDocument): boolean {
  const state = document.latest?.state;
  return state === 'pending_approval' || state === 'approved';
}

/** Ağaç yalnız bekleyen dokümanlar ve onları taşıyan klasörlerle. */
export function pendingTree(tree: LibraryTree): LibraryTree {
  const documents = tree.documents.filter(awaitingApproval);
  const parents = new Map(tree.folders.map((f) => [f.id, f.parentId]));
  const keep = new Set<string>();
  for (const document of documents) {
    for (let id: string | null | undefined = document.folderId; id; id = parents.get(id)) {
      keep.add(id);
    }
  }
  return { ...tree, documents, folders: tree.folders.filter((f) => keep.has(f.id)) };
}

// ---------------------------------------------------------------------------------------------
// Form Categories: M8a3'te salt okunur klasör ağacı (düzenleme M8a4; ADR 0037 §11)
// ---------------------------------------------------------------------------------------------

/** Klasördeki doküman sayısı (yalnız doğrudan içindekiler). */
export function folderDocumentCount(tree: LibraryTree, folderId: string): number {
  return tree.documents.filter((d) => d.folderId === folderId).length;
}

/** Kardeşleri sırasıyla (sıra, sonra kod). */
export function siblingsOf(
  folders: readonly LibraryFolder[],
  parentId: string | null,
): LibraryFolder[] {
  return folders
    .filter((f) => f.parentId === parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'en'));
}

// ---------------------------------------------------------------------------------------------
// Dağıtım panosu (ADR 0032 F6)
// ---------------------------------------------------------------------------------------------

export type CellStatus =
  | 'out_of_scope'
  | 'no_current'
  | 'missing'
  | 'failed'
  | 'stale'
  | 'deferred'
  | 'downloading'
  | 'upcoming'
  | 'current';

/**
 * Gemi hücresinin durumu: kapsam dışı, yürürlükte revizyon yok, gemiye hiç gelmemiş, indirme
 * başarısız, gemide eski revizyon, kota ya da disk yüzünden ertelendi, iniyor, sonraki
 * (zamanlanmış) revizyon önceden indi ya da yürürlükteki revizyon doğrulanmış.
 *
 * Sunucu hücreye geminin en büyük sıralı makbuzunu koyar: gemi zamanlanmış revizyonu da indirir
 * (`publishedAt !== null`), bu durumda gemi geride değil ileridedir (inceleme M8a3-R4).
 */
export function cellStatus(cell: DistributionCell, row: DistributionRow): CellStatus {
  if (!cell.inScope) return 'out_of_scope';
  if (!row.current) return 'no_current';
  const receipt = cell.receipt;
  if (!receipt) return 'missing';
  if (receipt.status === 'failed') return 'failed';
  if (receipt.state === 'scheduled') return 'upcoming';
  if (!receipt.current) return 'stale';
  if (receipt.status === 'deferred_quota') return 'deferred';
  return receipt.onBoard ? 'current' : 'downloading';
}

/**
 * Yürürlükteki revizyonun teyidi (inceleme M8a3-R3): sunucu geminin en büyük sıralı teyidini
 * verir; önceki revizyonun teyidi yürürlükteki revizyonun benimsendiğini göstermez [ISM 11.2].
 */
export function currentAcknowledgement(
  cell: DistributionCell,
): DistributionCell['acknowledgement'] {
  return cell.acknowledgement?.current ? cell.acknowledgement : null;
}

/** Vurgulanan hücreler: eksik, başarısız, eski, ertelenmiş ya da yürürlükteki revizyonun geç teyidi. */
export function cellHasIssue(cell: DistributionCell, row: DistributionRow): boolean {
  const status = cellStatus(cell, row);
  return (
    status === 'missing' ||
    status === 'failed' ||
    status === 'stale' ||
    status === 'deferred' ||
    (currentAcknowledgement(cell)?.late ?? false)
  );
}

export function distributionIssues(row: DistributionRow): number {
  return row.cells.filter((cell) => cellHasIssue(cell, row)).length;
}

export type DistributionSortColumn = 'docNo' | 'title' | 'issues';
export interface DistributionSort {
  column: DistributionSortColumn;
  direction: 'asc' | 'desc';
}

export function sortDistribution(
  rows: readonly DistributionRow[],
  sort: DistributionSort,
): DistributionRow[] {
  const sign = sort.direction === 'asc' ? 1 : -1;
  const text = (a: string, b: string) => a.localeCompare(b, 'en', { numeric: true });
  return [...rows].sort((a, b) => {
    let order = 0;
    if (sort.column === 'docNo') order = text(a.docNo, b.docNo) || text(a.title, b.title);
    else if (sort.column === 'title') order = text(a.title, b.title);
    else order = distributionIssues(a) - distributionIssues(b) || text(a.docNo, b.docNo);
    return order * sign;
  });
}

// ---------------------------------------------------------------------------------------------
// Büyük dosya (ADR 0028 EK madde 3)
// ---------------------------------------------------------------------------------------------

/** Bu boyutun üstünde ofis uyarır: dosya gemide her durumda toplu indirme penceresini bekler. */
export const LARGE_FILE_BYTES = 256 * 1024 * 1024;

/** Büyük dosyanın gemiye yaklaşık iniş süresi (saat, 1 Mbps varsayımıyla); küçükse `null`. */
export function largeFileHours(bytes: number): number | null {
  if (bytes <= LARGE_FILE_BYTES) return null;
  return (bytes * 8) / 1e6 / 3600;
}

/**
 * Yayın penceresinde seçilebilir yürürlük günleri (inceleme WF-5): sunucunun kuralıyla aynı,
 * en erken UTC yarın, en geç 365 gün sonra (`assertEffectiveOn`; ADR 0028 #27). Bugün seçilirse
 * sunucu parola girildikten sonra `EFFECTIVE_DATE_IN_PAST` döner; boş bırakmak "hemen"dir.
 */
export function publishDateBounds(now: string): { min: string; max: string } {
  const today = now.slice(0, 10);
  return { min: addDays(today, 1), max: addDays(today, MAX_EFFECTIVE_DAYS_AHEAD) };
}
